import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import DefaultTextField from 'components/Utils/DefaultTextField';
import TimeIntervalField from 'components/Utils/TimeIntervalField';
import Icon from 'components/Utils/NewIcon';
import StackItemsView from 'components/Common/StackItemsView';
import {
  ConcernRequirementSelector,
  ConcernRequirementBadge,
} from 'components/ConcernRequirements';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';

const PatientClearancesEditor = ({ clearances, onChange }) => {
  const dispatch = useAppDispatch();
  const requirementsById = useAppSelector((state) => state.concernRequirements.byId);
  const requirementsLoaded = useAppSelector((state) => state.concernRequirements.loaded);
  const [selectedId, setSelectedId] = useState('');
  const [remainingUses, setRemainingUses] = useState('');
  const [expiresAfter, setExpiresAfter] = useState(null);
  const [minimumInterval, setMinimumInterval] = useState(null);

  const allRequirements = useMemo(() => {
    return _(requirementsById).values().orderBy(['label'], ['asc']).value();
  }, [requirementsById]);

  useEffect(() => {
    if (allRequirements.length === 0) {
      dispatch(api.Appointments.getConcernRequirements(), { errorTitle: 'Clearances' });
    }
  }, [dispatch, allRequirements.length]);

  const handleAdd = () => {
    if (!selectedId) return;
    if (clearances.some((c) => c.clearance_id === selectedId)) return;
    const entry = { clearance_id: selectedId };
    if (remainingUses !== '') {
      entry.remaining_uses = parseInt(remainingUses, 10);
    }
    if (expiresAfter != null) {
      entry.expires_after = expiresAfter;
    }
    if (minimumInterval != null) {
      entry.minimum_interval = minimumInterval;
    }
    onChange([...clearances, entry]);
    setSelectedId('');
    setRemainingUses('');
    setExpiresAfter(null);
    setMinimumInterval(null);
  };

  const handleRemove = (item) => {
    onChange(clearances.filter((c) => c.clearance_id !== item.clearance_id));
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <ConcernRequirementSelector
          label="Clearance"
          name="patient_clearance_id"
          value={selectedId}
          onChange={(e) => {
            const id = e.target.value;
            setSelectedId(id);
            const req = allRequirements.find((r) => r.id === id);
            if (req?.default_uses != null) {
              setRemainingUses(String(req.default_uses));
            }
            if (req?.default_expires_after != null) {
              setExpiresAfter(req.default_expires_after);
            }
          }}
          excludeIds={clearances.map((c) => c.clearance_id)}
        />
        <DefaultTextField
          name="remaining_uses"
          label="Uses"
          type="number"
          value={remainingUses}
          onChange={(e) => setRemainingUses(e.target.value)}
          style={{ width: '80px' }}
          placeholder="∞"
        />
        <TimeIntervalField
          label="Expires after"
          value={expiresAfter}
          onChange={setExpiresAfter}
          min_interval="days"
          valueWidth="100px"
        />
        {expiresAfter != null && (
          <Icon
            src="circle-x"
            width="16px"
            alt="Clear expiry"
            showTooltip
            onClick={() => setExpiresAfter(null)}
            style={{ cursor: 'pointer' }}
          />
        )}
        <TimeIntervalField
          label="Minimum interval"
          value={minimumInterval}
          onChange={setMinimumInterval}
          min_interval="days"
          valueWidth="150px"
        />
        {minimumInterval != null && (
          <Icon
            src="circle-x"
            width="16px"
            alt="Clear minimum interval"
            showTooltip
            onClick={() => setMinimumInterval(null)}
            style={{ cursor: 'pointer' }}
          />
        )}
        <Icon
          src="circle-plus"
          width="20px"
          alt={selectedId ? 'Add clearance' : 'Select a clearance first'}
          showTooltip
          disabled={!selectedId}
          onClick={handleAdd}
          style={{ cursor: selectedId ? 'pointer' : 'not-allowed' }}
        />
      </div>
      {requirementsLoaded && allRequirements.length === 0 && (
        <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '8px', maxWidth: '320px' }}>
          No clearance types have been configured for this clinic yet. An administrator must create
          them under Concern Requirements before clearances can be granted here.
        </div>
      )}
      {clearances.length > 0 && (
        <StackItemsView
          stack={clearances}
          itemKey="clearance_id"
          describeItem={(clearance) => {
            const req = allRequirements.find((r) => r.id === clearance.clearance_id);
            const usesText =
              clearance.remaining_uses != null ? `${clearance.remaining_uses} uses` : 'unlimited';
            const expiresText =
              clearance.expires_after != null
                ? `expires after ${clearance.expires_after.value} ${clearance.expires_after.interval}`
                : 'no expiry';
            const minIntervalText =
              clearance.minimum_interval != null
                ? `, min interval ${clearance.minimum_interval.value} ${clearance.minimum_interval.interval}`
                : '';
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {req ? <ConcernRequirementBadge requirement={req} /> : clearance.clearance_id}
                <span style={{ fontSize: '0.75rem', color: '#888' }}>
                  ({usesText}, {expiresText}
                  {minIntervalText})
                </span>
              </div>
            );
          }}
          onDeleteItem={handleRemove}
          hideStatus
        />
      )}
    </div>
  );
};

export default PatientClearancesEditor;
